import { createReducer, PayloadAction } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';
import { persistReducer } from 'redux-persist';
import { defaultLogData } from 'src/utils/logs/logHelpers';
import { authActions } from '../auth/actions';
import { capacitorStorage } from '../persist';
import { FetchLogBasePayload, logActions } from './actions';
import { type LogState, type RemoteJobLogData } from './types';

const initialState: LogState = {
  remoteDataById: {},
  localDataByFilename: {},
};

const createDefaultInfo = (id: string, timestamp: number) => {
  const info: RemoteJobLogData = {
    id,
    totalLines: 1,
    parsedLines: 0,
    hasParseWarnings: false,
    hasParseErrors: false,
    requestTimestamp: timestamp,
  };
  return info;
};

/** Returns whether or not the provided timestamp is for an out-of-date request. */
const isStaleAction = (state: LogState, action: PayloadAction<FetchLogBasePayload>) => {
  const info = state.remoteDataById[action.payload.id];
  if (info === undefined) return false;
  return action.payload.timestamp < info.requestTimestamp;
};

const _logReducer = createReducer(initialState, builder =>
  builder
    .addCase(logActions.fetchLogStart, (state, action) => {
      if (isStaleAction(state, action)) return;
      state.remoteDataById[action.payload.id] = createDefaultInfo(action.payload.id, action.payload.timestamp);
    })
    .addCase(logActions.fetchLogDownloaded, (state, action) => {
      if (isStaleAction(state, action)) return;
      if (!state.remoteDataById[action.payload.id])
        state.remoteDataById[action.payload.id] = createDefaultInfo(action.payload.id, action.payload.timestamp);

      state.remoteDataById[action.payload.id].totalLines = action.payload.totalLines;
    })
    .addCase(logActions.fetchLogParseProgress, (state, action) => {
      if (isStaleAction(state, action)) return;
      if (!state.remoteDataById[action.payload.id])
        state.remoteDataById[action.payload.id] = createDefaultInfo(action.payload.id, action.payload.timestamp);

      state.remoteDataById[action.payload.id].parsedLines = action.payload.parsedLines;
    })
    .addCase(logActions.fetchLogSuccess, (state, action) => {
      if (isStaleAction(state, action)) return;
      if (!state.remoteDataById[action.payload.id])
        state.remoteDataById[action.payload.id] = createDefaultInfo(action.payload.id, action.payload.timestamp);

      state.remoteDataById[action.payload.id].parsedLines = state.remoteDataById[action.payload.id].totalLines;
      state.remoteDataById[action.payload.id].etag = action.payload.etag;
      state.remoteDataById[action.payload.id].data = action.payload.data;
      state.remoteDataById[action.payload.id].hasParseWarnings = action.payload.hasErrors;
    })
    .addCase(logActions.fetchLogFailure, (state, action) => {
      if (isStaleAction(state, action)) return;
      if (!state.remoteDataById[action.payload.id])
        state.remoteDataById[action.payload.id] = createDefaultInfo(action.payload.id, action.payload.timestamp);

      state.remoteDataById[action.payload.id].parsedLines = 0;
      state.remoteDataById[action.payload.id].hasParseErrors = true;
      state.remoteDataById[action.payload.id].etag = undefined;
    })
    .addCase(logActions.fetchLogReset, (state, action) => {
      if (isStaleAction(state, action)) return;
      state.remoteDataById[action.payload.id] = createDefaultInfo(action.payload.id, action.payload.timestamp);
    })
    .addCase(logActions.liveJobLogStreamBegin, (state, action) => {
      state.activeLogFileName = action.payload.filename;
    })
    .addCase(logActions.liveJobLogOpen, (state, action) => {
      const { payload } = action;
      state.localDataByFilename[payload.filename] = {
        filename: payload.filename,
        serialNumber: payload.serialNumber,
        data: defaultLogData(),
        hasParseErrors: false,
        hasParseWarnings: false,
        raw: '',
        pendingClose: false,
        createdAt: Date.now(),
        didPromptForMetadata: payload.skipMetadataCollection,
      };
    })
    .addCase(logActions.liveJobLogReplayDetected, (state, { payload }) => {
      if (!state.localDataByFilename[payload.filename]) {
        console.warn('Got a liveJobLogReplayDetected action, but no live state for the file:', payload.filename);
        return;
      }
      state.localDataByFilename[payload.filename].raw = '';
      state.localDataByFilename[payload.filename].data = defaultLogData();
      state.localDataByFilename[payload.filename].hasParseErrors = false;
      state.localDataByFilename[payload.filename].hasParseWarnings = false;
    })
    .addCase(logActions.liveJobLogInitializeStart, (state, { payload }) => {})
    .addCase(logActions.liveJobLogInitializeSuccess, (state, { payload }) => {
      if (!state.localDataByFilename[payload.filename]) {
        console.warn('Got a liveJobLogInitializeSuccess action, but no live state for the file:', payload.filename);
        return;
      }
      state.localDataByFilename[payload.filename].id = payload.info._id;
    })
    .addCase(logActions.liveJobLogInitializeFailure, (state, { payload }) => {})
    .addCase(logActions.liveJobLogUpdateMetadataStart, (state, { payload }) => {})
    .addCase(logActions.liveJobLogUpdateMetadataSuccess, (state, { payload }) => {
      if (!state.localDataByFilename[payload.filename]) {
        console.warn('Got a liveJobLogUpdateMetadataSuccess action, but no live state for the file:', payload.filename);
        return;
      }
      delete state.localDataByFilename[payload.filename].metadata;
    })
    .addCase(logActions.liveJobLogUpdateMetadataFailure, (state, { payload }) => {})
    .addCase(logActions.liveJobLogSyncStart, (state, { payload }) => {})
    .addCase(logActions.liveJobLogSyncSuccess, (state, { payload }) => {
      if (!state.localDataByFilename[payload.filename]) {
        console.warn('Got a liveJobLogSyncSuccess action, but no live state for the file:', payload.filename);
        return;
      }
    })
    .addCase(logActions.liveJobLogSyncFailure, (stat, { payload }) => {})
    .addCase(logActions.liveJobLogClose, (state, { payload }) => {
      if (state.activeLogFileName === payload.filename) state.activeLogFileName = undefined;
      if (!state.localDataByFilename[payload.filename]) {
        console.warn('Got a liveJobLogClose action, but no live state for the file:', payload.filename);
        return;
      }
      state.localDataByFilename[payload.filename].pendingClose = true;
    })
    .addCase(logActions.liveJobLogFinalizeStart, (state, { payload }) => {})
    .addCase(logActions.liveJobLogFinalizeSuccess, (state, { payload }) => {
      delete state.localDataByFilename[payload.filename]; // Once the log is finalized, we should just load it from remote as usual.
    })
    .addCase(logActions.liveJobLogFinalizeFailure, (state, { payload }) => {})
    .addCase(logActions.liveJobLogProgress, (state, { payload: { filename, data, error, raw } }) => {
      if (!state.localDataByFilename[filename]) {
        console.warn('Got a live action, but no live log is currently running for the file:', filename);
        return;
      }
      if (error) state.localDataByFilename[filename].hasParseWarnings = true; //payload.error is set when a line parse is caught and parsing continues.
      const nextRaw = state.localDataByFilename[filename].raw
        .split(/\r?\n/)
        .concat(raw.split(/\r?\n/))
        .filter(i => !!i.trim())
        .join('\n');
      state.localDataByFilename[filename].raw = nextRaw;
      state.localDataByFilename[filename].data = data;
    })
    .addCase(logActions.updateLogInfo, (state, { payload }) => {
      if (!payload?.filename || !state.localDataByFilename[payload.filename]) {
        console.error('Update log info fired with filename that does not match entry', payload?.filename);
        return;
      }

      state.localDataByFilename[payload.filename].metadata = payload.metadata;
      state.localDataByFilename[payload.filename].didPromptForMetadata = true;
    })
    .addCase(authActions.logOut, () => {
      return cloneDeep(initialState);
    })
    .addDefaultCase(state => state),
);

export const logReducer = persistReducer({ key: 'logs', storage: capacitorStorage, throttle: 3000 }, _logReducer);
